import { IconArrowUpRight } from "@ui/Icons/IconArrowUpRight";
import { IconInstagram } from "@ui/Icons/IconInstagram";
import { IconMail } from "@ui/Icons/IconMail";
import { IconSubstack } from "@ui/Icons/IconSubstack";
import { IconTiktok } from "@ui/Icons/IconTiktok";
import { IconYoutube } from "@ui/Icons/IconYoutube";
import type {
	SocialLink,
	SocialPlatform,
} from "@ui/Molecules/Social/SocialChips";
import type * as React from "react";

const SOCIAL_ICONS: Record<SocialPlatform, typeof IconInstagram> = {
	instagram: IconInstagram,
	tiktok: IconTiktok,
	youtube: IconYoutube,
	substack: IconSubstack,
};

export type AboutStoryContent = {
	intro: { paragraphs: React.ReactNode[]; pullQuote: string };
	spot: {
		eyebrow: string;
		title: string;
		body: string;
		image: string;
		imageAlt: string;
	};
	content: {
		title: string;
		body: string;
		image: string;
		imageAlt: string;
		socialLinks: SocialLink[];
		onNewsletter?: () => void;
	};
	contact?: { label: string; email: string; href?: string };
	copyright: string;
};

export function AboutStory({
	intro,
	spot,
	content,
	contact,
	copyright,
}: AboutStoryContent) {
	return (
		<div className="mx-auto flex max-w-[1360px] flex-col gap-3.5 px-6 py-10 md:gap-6 md:px-10 md:py-14">
			<div className="grid gap-3.5 md:grid-cols-[minmax(0,1.15fr)_minmax(0,1fr)] md:gap-6 md:items-start">
				<div className="flex flex-col gap-3.5 rounded-[var(--radius-lg)] bg-[var(--surface-default)] p-6 shadow-[var(--shadow-card)] md:gap-4 md:p-9">
					{intro.paragraphs.map((paragraph, i) => (
						<p
							// biome-ignore lint/suspicious/noArrayIndexKey: static prose
							key={i}
							className="m-0 text-[14.5px] leading-[1.68] md:text-[17px]"
						>
							{paragraph}
						</p>
					))}
					<div
						className="border-t border-[var(--border-default)] pt-4 text-[19px] font-extrabold leading-[1.25] tracking-[-0.02em] text-[var(--accent-500)] md:text-[30px]"
						style={{ fontFamily: "var(--font-display)" }}
					>
						{intro.pullQuote}
					</div>
				</div>

				<div className="overflow-hidden rounded-[var(--radius-lg)] bg-[var(--surface-default)] shadow-[var(--shadow-card)]">
					<img
						src={spot.image}
						alt={spot.imageAlt}
						className="block h-[214px] w-full object-cover md:h-[260px]"
						style={{ objectPosition: "50% 55%" }}
					/>
					<div className="flex flex-col gap-2.5 p-6 md:p-8">
						<div className="flex items-center gap-2">
							<span className="h-[7px] w-[7px] rounded-full bg-[var(--accent-500)]" />
							<span className="text-[11px] font-bold uppercase tracking-[0.07em] text-[var(--content-muted)]">
								{spot.eyebrow}
							</span>
						</div>
						<div
							className="text-[22px] font-extrabold tracking-[-0.025em] md:text-[28px]"
							style={{ fontFamily: "var(--font-display)" }}
						>
							{spot.title}
						</div>
						<p className="m-0 text-[14.5px] leading-[1.65] md:text-[16px]">
							{spot.body}
						</p>
					</div>
				</div>
			</div>

			<div className="overflow-hidden rounded-[var(--radius-lg)] bg-[var(--surface-inverted)] md:grid md:grid-cols-2">
				<img
					src={content.image}
					alt={content.imageAlt}
					className="block h-[246px] w-full object-cover md:h-full md:min-h-[340px]"
					style={{ objectPosition: "50% 42%" }}
				/>
				<div className="flex flex-col justify-center gap-3.5 p-6 md:gap-5 md:p-10">
					<div
						className="text-[22px] font-extrabold tracking-[-0.025em] text-white md:text-[32px]"
						style={{ fontFamily: "var(--font-display)" }}
					>
						{content.title}
					</div>
					<p className="m-0 max-w-[44ch] text-[14px] leading-[1.6] text-[rgb(255_255_255_/_0.72)] md:text-[16px]">
						{content.body}
					</p>
					<div className="grid grid-cols-2 gap-2 md:max-w-[420px]">
						{content.socialLinks.map((link) => {
							const Icon = SOCIAL_ICONS[link.platform];
							return (
								<a
									key={link.platform}
									href={link.href}
									target="_blank"
									rel="noopener noreferrer"
									onClick={
										link.platform === "substack" && content.onNewsletter
											? (e) => {
													e.preventDefault();
													content.onNewsletter?.();
												}
											: undefined
									}
									className="flex items-center gap-2.5 rounded-[var(--radius-sm)] border border-[var(--border-on-dark)] bg-[rgb(255_255_255_/_0.09)] px-4 py-3.5 text-[13px] font-bold text-white md:text-[14px]"
								>
									<Icon size={17} color="#fff" />
									{link.label}
								</a>
							);
						})}
					</div>
				</div>
			</div>

			{contact && (
				<a
					href={contact.href ?? `mailto:${contact.email}`}
					className="flex items-center gap-4 rounded-[var(--radius-lg)] bg-[var(--surface-default)] px-6 py-5 shadow-[var(--shadow-card)] md:px-8"
				>
					<span className="grid h-[42px] w-[42px] shrink-0 place-items-center rounded-[var(--radius-pill)] bg-[var(--surface-elevated)]">
						<IconMail size={18} color="var(--content-primary)" />
					</span>
					<span className="min-w-0 flex-1">
						<span
							className="block text-[15px] font-extrabold md:text-[16px]"
							style={{ fontFamily: "var(--font-display)" }}
						>
							{contact.label}
						</span>
						<span className="block text-[13px] text-[var(--content-muted)] md:text-[14px]">
							{contact.email}
						</span>
					</span>
					<IconArrowUpRight size={16} color="var(--content-faint)" />
				</a>
			)}

			<div className="pt-2 text-center text-[11.5px] text-[var(--content-faint)] md:text-[12.5px]">
				{copyright}
			</div>
		</div>
	);
}
